/**
 * ISO Platform — Design Tokens v2 (JavaScript export)
 * "Mediterranean Saffron, evolved"
 *
 * Drop-in replacement for styles/tokens.js. MUST stay in sync with _tokens.css.
 *
 * Use CSS variables for anything rendered as HTML/CSS.
 * Use these exports only where a library needs a literal value
 * (Chart.js, canvas, Leaflet, PDF generation) — or on React Native /
 * the supplier mobile app, where there are no CSS variables at all.
 */

// ===================================
// RAW RAMPS
// ===================================
export const palette = {
  saffron: { 50: '#fdf6e7', 100: '#fae8c4', 200: '#f5d492', 500: '#e2960c', 600: '#c77a05', 700: '#a05e03', 900: '#6b3d02' },
  aegean:  { 50: '#eaf3f8', 100: '#c9e2ee', 500: '#1e88ae', 600: '#146c8e', 700: '#0e5471' },
  olive:   { 50: '#f2f7e8', 100: '#d7e5bc', 600: '#4c7a18', 700: '#3a5e12' },
  clay:    { 50: '#fcefe9', 100: '#f5d2c1', 600: '#c1481b', 700: '#96380f' },
  rust:    { 50: '#fbecea', 100: '#f2c9c4', 600: '#a82016', 700: '#85180f' },
  stone:   { 0: '#ffffff', 25: '#fefdfb', 50: '#fbf8f3', 100: '#f6f2ec', 200: '#ede6dc', 300: '#ddd3c6', 400: '#c6b9a8', 500: '#988e84', 600: '#746a61', 700: '#554c44', 800: '#3b342e', 900: '#191512' }
};

// ===================================
// SURFACES
// ===================================
export const bgLayer1 = palette.stone[100];
export const bgLayer2 = palette.stone[50];
export const bgLayer3 = palette.stone[25];
export const bgLayer4 = palette.stone[0];

// ===================================
// INK — the hero surface
// Layer 0 is not "deeper" than layer 1; it is the focal
// surface. One hero per screen, and it owns the screen's
// single most important number.
// ===================================
export const ink = { 900: '#191512', 800: '#241f1a', 700: '#3b342e', eva: '#0e5471' };

export const hero = {
  surface: ink[900],
  tile: ink[800],
  hairline: ink[700],
  text: '#fbf8f3',
  textMuted: palette.stone[500],
  textDim: palette.stone[600],
  accent: palette.saffron[200],
  evaSurface: ink.eva,
  evaTile: 'rgba(255, 255, 255, 0.10)',
  evaText: palette.aegean[100],
  radius: 28
};

// ===================================
// TEXT
// ===================================
export const textPrimary = palette.stone[900];
export const textSecondary = palette.stone[800];
export const textTertiary = palette.stone[700];
export const textBody = palette.stone[700];
export const textMuted = palette.stone[600];
export const textLight = palette.stone[500];
export const textOnBrand = '#ffffff';

// ===================================
// BORDERS
// ===================================
export const borderLight = palette.stone[200];
export const borderMedium = palette.stone[300];
export const borderStrong = palette.stone[400];

// ===================================
// ACTION
// ===================================
export const colorPrimary = palette.saffron[600];
export const colorPrimaryHover = palette.saffron[700];
export const colorPrimaryLight = palette.saffron[50];

/** Eva only. Never use for generic UI. */
export const colorEva = palette.aegean[600];
export const colorEvaLight = palette.aegean[50];

// ===================================
// FEEDBACK
// ===================================
export const colorSuccess = palette.olive[600];
export const colorSuccessLight = palette.olive[50];
export const colorWarning = palette.clay[600];
export const colorWarningLight = palette.clay[50];
export const colorError = palette.rust[600];
export const colorErrorLight = palette.rust[50];
export const colorInfo = palette.aegean[600];
export const colorInfoLight = palette.aegean[50];

// ===================================
// BOOKING LIFECYCLE
// Canonical map. Both portals, the app and the API
// docs read from this — a status has one colour everywhere.
// ===================================
export const bookingStatus = {
  requested:   { fg: palette.aegean[700], bg: palette.aegean[50], border: palette.aegean[100], dot: palette.aegean[600], label: 'Requested' },
  pending:     { fg: palette.clay[700],   bg: palette.clay[50],   border: palette.clay[100],   dot: palette.clay[600],   label: 'Needs answer' },
  confirmed:   { fg: palette.olive[700],  bg: palette.olive[50],  border: palette.olive[100],  dot: palette.olive[600],  label: 'Confirmed' },
  alternative: { fg: palette.saffron[700], bg: palette.saffron[50], border: palette.saffron[200], dot: palette.saffron[600], label: 'Alternative proposed' },
  declined:    { fg: palette.rust[700],   bg: palette.rust[50],   border: palette.rust[100],   dot: palette.rust[600],   label: 'Declined' },
  cancelled:   { fg: palette.stone[600],  bg: palette.stone[100], border: palette.stone[200],  dot: palette.stone[500],  label: 'Cancelled' },
  completed:   { fg: palette.stone[700],  bg: palette.stone[100], border: palette.stone[200],  dot: palette.stone[600],  label: 'Completed' }
};

// ===================================
// CHART PALETTE
// Ordered for categorical series. First four are
// distinguishable in greyscale and for deuteranopia.
// ===================================
export const chartSeries = [
  palette.saffron[600],
  palette.aegean[600],
  palette.olive[600],
  palette.clay[600],
  palette.saffron[200],
  palette.aegean[100],
  palette.stone[400]
];
export const chartGrid = palette.stone[200];
export const chartAxisText = palette.stone[600];

// ===================================
// TYPOGRAPHY
// ===================================
export const fontSans = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
export const fontMono = "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace";

export const fontWeight = { normal: 400, medium: 500, semibold: 600, bold: 700 };

/** Desktop / web portals (px) */
export const fontSize = { '2xs': 11, xs: 12, sm: 13, base: 14, lg: 16, xl: 18, '2xl': 22, '3xl': 28, '4xl': 34 };

/** Supplier mobile app (px). Body never below 16. */
export const mobileFontSize = { xs: 13, sm: 15, base: 16, lg: 18, xl: 22, '2xl': 28, '3xl': 36 };

export const lineHeight = { tight: 1.2, normal: 1.45, relaxed: 1.6 };

/** Display numerals — hero only. Always mono, always tabular. */
export const displaySize = { sm: 34, md: 42, lg: 74 };
export const displayStyle = {
  fontFamily: fontMono,
  fontWeight: 500,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.045em',
  lineHeight: 0.94
};

// ===================================
// SPACING / RADIUS / ICONS
// ===================================
export const space = { '2xs': 2, xs: 4, sm: 8, md: 12, lg: 16, xl: 20, '2xl': 24, '3xl': 32, '4xl': 48 };
export const radius = { sm: 6, md: 8, lg: 10, xl: 14, '2xl': 20, hero: 28, sheet: 26, input: 8, full: 999 };
export const iconSize = { xs: 14, sm: 16, md: 20, lg: 24, xl: 32 };
export const iconStroke = 1.75;

// ===================================
// LAYOUT
// ===================================
export const layout = {
  sidebarExpanded: 232,
  sidebarCollapsed: 48,
  sidebarGap: 16,
  topNavHeight: 56,
  contentMaxWidth: 1440,
  mobileTapMin: 48,
  mobileGutter: 16,
  mobileBottomNavHeight: 64
};

// ===================================
// ELEVATION & MOTION
// ===================================
export const shadow = {
  xs: '0 1px 2px 0 rgba(107, 61, 2, 0.05)',
  sm: '0 1px 3px 0 rgba(107, 61, 2, 0.07), 0 1px 2px 0 rgba(107, 61, 2, 0.04)',
  md: '0 4px 8px -2px rgba(107, 61, 2, 0.08), 0 2px 4px -2px rgba(107, 61, 2, 0.04)',
  lg: '0 12px 20px -6px rgba(107, 61, 2, 0.10), 0 4px 8px -4px rgba(107, 61, 2, 0.05)',
  xl: '0 24px 40px -12px rgba(107, 61, 2, 0.14)'
};

export const duration = { fast: 120, base: 200, slow: 320 };
export const easing = {
  out: 'cubic-bezier(0, 0, 0.2, 1)',
  in: 'cubic-bezier(0.4, 0, 1, 1)',
  inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  emphasis: 'cubic-bezier(0.25, 0.8, 0.25, 1)'
};

// ===================================
// HELPERS
// ===================================
/** Status chip styles for a booking status key. */
export function statusChip(status) {
  return bookingStatus[status] || bookingStatus.cancelled;
}

/** Money is always mono + tabular in tables and ledgers. */
export const moneyStyle = { fontFamily: fontMono, fontVariantNumeric: 'tabular-nums' };

/**
 * Slide-to-accept geometry. Used for irreversible actions taken
 * outdoors — a deliberate drag cannot be fired by a wet thumb.
 * Track fills saffron-600 → olive-600 across the travel.
 */
export const slideToAccept = {
  height: 64,
  knob: 54,
  knobRadius: 14,
  trackRadius: 18,
  commitFraction: 0.72,
  from: palette.saffron[600],
  to: palette.olive[600]
};
